import Dialog from "../../../components/Dialog";
import { Organization } from "../../../services/organization/organizationModel";
import { useTranslation } from "react-i18next";
import SxForm from "../../../forms/SxForm";
import SxField from "../../../forms/fields/SxField";
import SubmitButton from "../../../components/SubmitButton";
import ValidationErrors from "../../../forms/ValidationErrors";
import { object, string } from "yup";
import { useMemo, useState } from "react";
import {
  countAvailableSponsorshipCredits,
  premiumSponsorshipService,
} from "../../../services/premiumSponsorship/premiumSponsorshipService";
import { PremiumSponsorship } from "../../../services/premiumSponsorship/premiumSponsorshipModel";
import useReload from "../../../hooks/useReload";
import { loggerBuilder } from "../../../services/logger";

interface Props {
  onClose(): void;
  organization: Organization;
  sponsorships: PremiumSponsorship[];
}

const { invitePremiumSponsorships } = premiumSponsorshipService();
const logger = loggerBuilder("invite-premium-sponsorships-dialog");

const InvitePremiumSponsorshipsDialog = ({
  onClose,
  organization,
  sponsorships,
}: Props) => {
  const { t } = useTranslation(["organization"]);
  const reload = useReload();
  const [loading, setLoading] = useState(false);
  const availableCredits = useMemo(
    () => countAvailableSponsorshipCredits(organization, sponsorships),
    [organization, sponsorships],
  );

  return (
    <Dialog onClose={onClose} className="sp-dialog">
      <>
        <div className="popup_head">
          <div className="popup_title">
            {t("organization:premium-sponsorship.invite.TITLE")}
          </div>
        </div>

        <div className="popup_body">
          <p className="ui-chip --ok --nod ui-num sp-invite_credits">
            {t("organization:premium-sponsorship.invite.CREDITS_AVAILABLE", {
              replace: { count: availableCredits },
            })}
          </p>
          <SxForm
            initialValues={{ emails: "" }}
            validationSchema={object({
              emails: string()
                .label(t("organization:premium-sponsorship.invite.EMAILS"))
                .required(
                  t(
                    "organization:premium-sponsorship.invite.errors.EMAILS_REQUIRED",
                  ),
                )
                .test(
                  "valid-emails",
                  t(
                    "organization:premium-sponsorship.invite.errors.INVALID_EMAILS",
                  ),
                  (value) => {
                    if (!value) return false;
                    const emails = value
                      .split(/\r?\n/)
                      .map((e) => e.trim())
                      .filter(Boolean);
                    if (emails.length === 0) return false;
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emails.every((e) => emailRegex.test(e));
                  },
                )
                .test(
                  "enough-credits",
                  t(
                    "organization:premium-sponsorship.invite.errors.NOT_ENOUGH_CREDITS",
                  ),
                  (value) => {
                    if (!value) return true;
                    const emails = value
                      .split(/\r?\n/)
                      .map((e) => e.trim())
                      .filter(Boolean);
                    return emails.length <= availableCredits;
                  },
                ),
            })}
            onSubmit={async (values) => {
              const emails = values.emails
                .split(/\r?\n/)
                .map((e) => e.trim())
                .filter(Boolean);

              if (emails.length === 0) return;

              setLoading(true);
              try {
                await invitePremiumSponsorships(organization.id, emails);
                reload();
                onClose();
              } catch (err) {
                logger.error(err);
              } finally {
                setLoading(false);
              }
            }}
          >
            <SxField
              name="emails"
              as="textarea"
              placeholder={t(
                "organization:premium-sponsorship.invite.PLACEHOLDER_EMAILS",
              )}
              label={t("organization:premium-sponsorship.invite.EMAILS")}
            />

            <ValidationErrors />

            <div className="form_footer --txt--right">
              <SubmitButton className="btn" disabled={loading}>
                {t("organization:premium-sponsorship.invite.SEND_BUTTON")}
              </SubmitButton>
            </div>
          </SxForm>
        </div>
      </>
    </Dialog>
  );
};

export default InvitePremiumSponsorshipsDialog;
